import * as React from 'react';
import {useSelector} from 'react-redux';
import {
  getSubtractedDate,
  getTodayInTimezone,
} from '@spaced-out/ui-design-system/lib/utils';

import {selectAgencyData} from 'src/selectors/agency';
import {useQueryObject} from 'src/hooks/useQueryObject';
import type {DateRange} from 'src/types/byod';


const QUERY_KEY = 'byod_date_range';

type UseByodDateRangeReturn = readonly [DateRange, (next: DateRange) => void];

// Every BYOD consumer (the picker in DashboardBuilder, useTileData per tile,
// and the edit panel) calls this hook with the same query key, so they all
// read and write the same slot. No store, no context, no prop drilling.
//
// Side benefit: the selected range is shareable (copy URL → same range) and
// browser back/forward steps through range changes.
export function useByodDateRange(): UseByodDateRangeReturn {
  const {timezone: agencyTimezone} = useSelector(selectAgencyData);

  // Default: last 7 days in the agency timezone. Memoized so the reference
  // is stable across renders within a consumer — useQueryObject's
  // defaultValue should be stable to avoid redundant parses.
  const defaultDateRange = React.useMemo<DateRange>(() => {
    const today = getTodayInTimezone(agencyTimezone);
    return {
      startDate: getSubtractedDate(today, 6, 'd'),
      endDate: today,
      timezone: agencyTimezone,
    };
  }, [agencyTimezone]);

  const [dateRange, update] = useQueryObject<DateRange>(
    QUERY_KEY,
    defaultDateRange,
  );

  // Wrap the heterogeneous setter from useQueryObject in a single-shape
  // setter so callers don't have to think about partial-vs-section forms.
  const setDateRange = React.useCallback(
    (next: DateRange) => update(next),
    [update],
  );

  return [dateRange, setDateRange] as const;
}
